/**
 * YouTubeAdMockup Component
 *
 * YouTube video ad mockup
 * Skippable in-stream ad format
 */

import React from 'react';
import { stripMediaTags } from '@/utils/mediaTagParser';
import { AdMockupContainer } from '../base/AdMockupContainer';
import { AdMockupMedia } from '../base/AdMockupMedia';
import { AdMockupCTA } from '../base/AdMockupCTA';
import { YouTubeAdMockupProps } from '../types/platform.types';

export const YouTubeAdMockup: React.FC<YouTubeAdMockupProps> = ({
  thumbnailUrl,
  headline,
  headlines,
  description,
  descriptions,
  callToAction = 'Learn More',
  brandName = 'Your Brand',
  brandLogoUrl,
  duration = '0:15',
  skippable = true,
}) => {
  // Support both singular and plural props for backward compatibility
  const allHeadlines = headlines || (headline ? [headline] : []);
  const allDescriptions = descriptions || (description ? [description] : []);
  // Strip MEDIA tags from text before display (thumbnail shown separately above)
  const displayHeadline = stripMediaTags(allHeadlines[0] || '');
  const displayDescription = stripMediaTags(allDescriptions[0] || '');

  return (
    <AdMockupContainer platform="youtube">
      <div className="rounded-lg border-2 border-gray-300 dark:border-border-dark overflow-hidden bg-black shadow-sm">
        <div className="relative aspect-video">
          {thumbnailUrl ? (
            <AdMockupMedia
              mediaUrl={thumbnailUrl}
              aspectRatio="16:9"
              platform="youtube"
              alt="YouTube video thumbnail"
              showPlayButton={true}
            />
          ) : (
            <div className="w-full h-full bg-gray-800 flex items-center justify-center">
              <svg className="w-16 h-16 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
              </svg>
            </div>
          )}

          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

          {/* Ad Label */}
          <div className="absolute top-4 left-4 px-2 py-1 bg-yellow-400 text-black text-xs font-bold rounded">
            Ad
          </div>

          {/* Skip Button */}
          {skippable && (
            <div className="absolute top-4 right-4 px-3 py-1.5 bg-black/60 text-white text-sm font-medium rounded backdrop-blur-sm">
              Skip Ad in 5s
            </div>
          )}

          {/* Bottom Content */}
          <div className="absolute bottom-4 left-4 right-4">
            <p className="text-white text-sm font-semibold mb-2 line-clamp-2">{displayHeadline}</p>
            <AdMockupCTA
              text={callToAction}
              platform="youtube"
              variant="primary"
              fullWidth={false}
            />
          </div>
        </div>

        {/* Video Info */}
        <div className="p-4 flex items-start gap-4 bg-white dark:bg-background-dark">
          <div className="flex-shrink-0">
            {brandLogoUrl ? (
              <img
                src={brandLogoUrl}
                alt={brandName}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center text-white font-bold">
                {brandName.slice(0, 2).toUpperCase()}
              </div>
            )}
          </div>
          <div className="flex-1">
            <p className="font-semibold text-gray-900 dark:text-white">{displayHeadline}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Ad · {brandName}
            </p>
            {displayDescription && (
              <p className="text-sm text-gray-700 dark:text-gray-300 mt-2 line-clamp-2">{displayDescription}</p>
            )}

            {/* Show additional headline variations */}
            {allHeadlines.length > 1 && (
              <div className="mt-3 pt-2 border-t border-gray-200 dark:border-border-dark">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
                  📌 {allHeadlines.length - 1} More Headline{allHeadlines.length > 2 ? 's' : ''}:
                </p>
                {allHeadlines.slice(1, 3).map((h, idx) => (
                  <p key={idx} className="text-xs text-gray-600 dark:text-gray-400 mb-1 pl-2 border-l-2 border-red-300 dark:border-red-600">
                    {h}
                  </p>
                ))}
                {allHeadlines.length > 3 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                    +{allHeadlines.length - 3} more...
                  </p>
                )}
              </div>
            )}

            {/* Show additional description variations */}
            {allDescriptions.length > 1 && (
              <div className="mt-2">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
                  📝 {allDescriptions.length - 1} More Description{allDescriptions.length > 2 ? 's' : ''}:
                </p>
                {allDescriptions.slice(1, 2).map((d, idx) => (
                  <p key={idx} className="text-xs text-gray-600 dark:text-gray-400 mb-1 line-clamp-2">
                    {d}
                  </p>
                ))}
                {allDescriptions.length > 2 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                    +{allDescriptions.length - 2} more...
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </AdMockupContainer>
  );
};

export default YouTubeAdMockup;
